import { GridActionsCellItem, GridColDef, GridComparatorFn } from "@mui/x-data-grid-premium";
import PolicyIcon from "@mui/icons-material/Policy";
import { Chip, Typography } from "@mui/material";

import { amber, green, grey, red } from "@mui/material/colors";
import _ from "lodash";
import { PrivatePolicyValidationStatus } from "../../../reports/useReportsStore/privatePolicyReportSlice";
import dayjs from "dayjs";

export const validationStatusLabels = {
  [PrivatePolicyValidationStatus.accepted]: "Accepted",
  [PrivatePolicyValidationStatus.pendingRejected]: "Pending - Rejected",
  [PrivatePolicyValidationStatus.invalidExpiration]: "Invalid Expiration",
  [PrivatePolicyValidationStatus.invalidNumber]: "Invalid Number",
  [PrivatePolicyValidationStatus.unknownDeclaration]: "Declaration Not Uploaded",
  [PrivatePolicyValidationStatus.rejected]: "Rejected",
  [PrivatePolicyValidationStatus.pending]: "Pending",
  [PrivatePolicyValidationStatus.humanReview]: "Under Review",
};

export const validationStatusColors = {
  [PrivatePolicyValidationStatus.accepted]: green[500],

  [PrivatePolicyValidationStatus.rejected]: red[500],
  [PrivatePolicyValidationStatus.pendingRejected]: amber[500],

  [PrivatePolicyValidationStatus.invalidExpiration]: red[400],
  [PrivatePolicyValidationStatus.invalidNumber]: red[400],

  [PrivatePolicyValidationStatus.humanReview]: amber[500],
  [PrivatePolicyValidationStatus.unknownDeclaration]: amber[500],
};

/* Used for determining the default order to display private policy validation statuses in */
const validationStatusComparator: GridComparatorFn<PrivatePolicyValidationStatus> = (
  statusA: PrivatePolicyValidationStatus,
  statusB: PrivatePolicyValidationStatus,
) => {
  const order = [
    PrivatePolicyValidationStatus.rejected,
    PrivatePolicyValidationStatus.pendingRejected,
    PrivatePolicyValidationStatus.invalidNumber,
    PrivatePolicyValidationStatus.invalidExpiration,
    PrivatePolicyValidationStatus.unknownDeclaration,
    PrivatePolicyValidationStatus.humanReview,
    PrivatePolicyValidationStatus.pending,
    PrivatePolicyValidationStatus.accepted,
  ];

  return order.indexOf(statusA) - order.indexOf(statusB);
};

export const basePrivatePolicyReportColumnDefs: GridColDef[] = [
  {
    field: "tenantName",
    headerName: "Tenant",
    flex: 1,
    renderHeader: (headerParams) => {
      return (
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {headerParams.colDef.headerName}
        </Typography>
      );
    },
  },
  {
    field: "locationName",
    headerName: "Facility Name",
    flex: 1,
  },
  {
    field: "locationCity",
    headerName: "City",
  },
  {
    field: "locationState",
    headerName: "State",
  },
  {
    field: "locationPostalCode",
    headerName: "Zip Code",
  },
  {
    field: "unitName",
    headerName: "Unit Name",
  },
  {
    field: "privatePolicySource",
    headerName: "Source",
    flex: 1,
  },
  {
    field: "privatePolicyNumber",
    headerName: "Policy Number",
    flex: 1,
  },
  {
    field: "privatePolicyCompanyName",
    headerName: "Company Name",
    flex: 1,
  },
  {
    field: "privatePolicyExpiration",
    headerName: "Expiration Date",
    flex: 1,
    headerAlign: "right",
    align: "right",
    renderCell: (params) => {
      if (!params.value) return null;
      return dayjs(params.value).format("MM/DD/YYYY");
    },
  },
  {
    field: "privatePolicyValidationStatus",
    headerName: "Validation Status",
    align: "center",
    headerAlign: "center",
    sortComparator: validationStatusComparator,
    flex: 1,
    renderCell: (params) => {
      if (!validationStatusLabels[params.value]) return null;

      return (
        <Chip
          sx={{
            bgcolor: validationStatusColors[params.value] ?? grey[500],
            color: "white",
            fontWeight: "bold",
          }}
          size="small"
          label={validationStatusLabels[params.value] ?? "pending"}
        />
      );
    },
  },
  {
    field: "attachment",
    headerName: "Details",
    type: "actions",
    getActions: (params) => {
      if (params.id.toString().startsWith("auto-generated-row")) return [];

      return [<GridActionsCellItem icon={<PolicyIcon />} label="Edit" onClick={() => params.row.handleOpenDeclarationsPageDialog()} />];
    },
  },
];

export const privatePolicyReportColumnDefs: GridColDef[] = basePrivatePolicyReportColumnDefs.map((colDef) => ({
  ...colDef,
  renderHeader: (headerParams) => {
    return (
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {headerParams.colDef.headerName}
      </Typography>
    );
  },
}));
